import { formatBrl } from '@estac1/shared';
import { Card, CardContent } from '@estac1/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import type { CapacityUpsellResponse } from './types.js';

/**
 * Lots that keep hitting their declared vacancy cap (#229). An honest upsell
 * signal: opens are refused at the cap, so a lot repeatedly at the ceiling
 * likely has more real capacity than it declared. Hidden when the feature
 * toggle is off; nothing shown when there are no candidates.
 */
export const CapacityUpsellCard = () => {
  const q = useQuery({
    queryKey: ['admin-capacity-upsell'],
    queryFn: () => api.request<CapacityUpsellResponse>('/v1/admin/capacity-upsell'),
  });

  if (q.isLoading || !q.data || !q.data.enabled || q.data.candidates.length === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div>
          <h2 className="text-sm font-semibold">Oportunidades de upsell por capacidade</h2>
          <p className="text-xs text-muted-fg">
            Lotes que bateram o limite de vagas em ≥ {q.data.config.minDaysAtCap} dias nos últimos{' '}
            {q.data.config.windowDays}. Provavelmente têm mais capacidade real do que declararam.
          </p>
        </div>
        <ul className="divide-y divide-border lg:hidden">
          {q.data.candidates.map((r) => (
            <li key={r.lotId} className="space-y-1 py-3 text-sm">
              <p className="font-medium">{r.lotName}</p>
              <p className="text-xs text-muted-fg">
                {r.vacancies} vagas · pico {r.peakOpen} ·{' '}
                <span className="font-semibold text-warning">{r.daysAtCap} dias no limite</span>
              </p>
              <p className="text-xs tabular-nums">
                Sugerido {r.suggestedVacancies} vagas ·{' '}
                {r.currentMrrCents != null ? formatBrl(r.currentMrrCents) : '—'}
                {' → '}
                {r.suggestedMonthlyCents != null ? formatBrl(r.suggestedMonthlyCents) : '—'}
              </p>
            </li>
          ))}
        </ul>
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-fg">
                <th className="px-3 py-2 font-medium">Estacionamento</th>
                <th className="px-3 py-2 text-right font-medium">Vagas atuais</th>
                <th className="px-3 py-2 text-right font-medium">Pico</th>
                <th className="px-3 py-2 text-right font-medium">Dias no limite</th>
                <th className="px-3 py-2 text-right font-medium">Sugerido</th>
                <th className="px-3 py-2 text-right font-medium">MRR atual → sugerido</th>
              </tr>
            </thead>
            <tbody>
              {q.data.candidates.map((r) => (
                <tr key={r.lotId} className="border-b border-border last:border-0">
                  <td className="px-3 py-2">{r.lotName}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.vacancies}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.peakOpen}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-warning">
                    {r.daysAtCap}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.suggestedVacancies}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.currentMrrCents != null ? formatBrl(r.currentMrrCents) : '—'}
                    {' → '}
                    {r.suggestedMonthlyCents != null ? formatBrl(r.suggestedMonthlyCents) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
